import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { getLocalApiUrl, authFetch } from '../../config/api'
import { useForm } from 'react-hook-form'
import RuleRiskLinksPanel from './components/RuleRiskLinksPanel'
import { PROCESSING_CATEGORY_LEGACY_FORM_OPTIONS } from '../../utils/processingCategory'
import {
  DocumentTextIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  BeakerIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

// Transaction-specific fields
const TRANSACTION_FIELDS = [
  // Financial Fields
  { id: 'CashIn', label: 'Cash In Amount', type: 'number', category: 'Financial' },
  { id: 'CashOut', label: 'Cash Out Amount', type: 'number', category: 'Financial' },
  { id: 'NetAmount', label: 'Net Amount', type: 'number', category: 'Financial' },
  { id: 'TransactionAmount', label: 'Transaction Amount', type: 'number', category: 'Financial' },
  { id: 'CumulativeAmount', label: 'Cumulative Amount', type: 'number', category: 'Financial' },
  
  // Transaction Details
  { id: 'TransactionType', label: 'Transaction Type', type: 'text', category: 'Transaction Details' },
  { id: 'PaymentMethod', label: 'Payment Method', type: 'text', category: 'Transaction Details' },
  { id: 'Currency', label: 'Currency', type: 'text', category: 'Transaction Details' },
  { id: 'TransactionStatus', label: 'Transaction Status', type: 'text', category: 'Transaction Details' },
  { id: 'TransactionDate', label: 'Transaction Date', type: 'date', category: 'Transaction Details' },
  { id: 'TransactionTime', label: 'Transaction Time', type: 'time', category: 'Transaction Details' },
  
  // Member Information
  { id: 'MemberID', label: 'Player ID', type: 'text', category: 'Member' },
  { id: 'AccountID', label: 'Account ID', type: 'text', category: 'Member' },
  { id: 'MemberTier', label: 'Member Tier', type: 'text', category: 'Member' },
  { id: 'MemberStatus', label: 'Member Status', type: 'text', category: 'Member' },
  
  // Location & Device
  { id: 'VenueID', label: 'Venue ID', type: 'text', category: 'Location' },
  { id: 'TerminalID', label: 'Terminal ID', type: 'text', category: 'Location' },
  { id: 'DeviceType', label: 'Device Type', type: 'text', category: 'Location' },
  { id: 'IPAddress', label: 'IP Address', type: 'text', category: 'Location' },
  
  // Risk & Compliance
  { id: 'RiskScore', label: 'Risk Score', type: 'number', category: 'Risk & Compliance' },
  { id: 'AMLFlag', label: 'AML Flag', type: 'boolean', category: 'Risk & Compliance' },
  { id: 'SuspiciousFlag', label: 'Suspicious Flag', type: 'boolean', category: 'Risk & Compliance' },
  { id: 'ComplianceStatus', label: 'Compliance Status', type: 'text', category: 'Risk & Compliance' },
]

const OPERATORS = [
  { value: '>', label: 'Greater Than (>)' },
  { value: '>=', label: 'Greater Than or Equal (>=)' },
  { value: '<', label: 'Less Than (<)' },
  { value: '<=', label: 'Less Than or Equal (<=)' },
  { value: '=', label: 'Equals (=)' },
  { value: '!=', label: 'Not Equals (!=)' },
  { value: 'contains', label: 'Contains' },
  { value: 'startsWith', label: 'Starts With' },
  { value: 'endsWith', label: 'Ends With' },
  { value: 'in', label: 'In List' },
  { value: 'between', label: 'Between' },
]

function mapBackendToUiOperator(op) {
  switch (op) {
    case 'greater_than': return '>'
    case 'greater_than_or_equal': return '>='
    case 'less_than': return '<'
    case 'less_than_or_equal': return '<='
    case 'equals': return '='
    case 'not_equals': return '!='
    default: return op
  }
}

const TRANSACTION_TYPES = [
  'CASH_IN', 'CASH_OUT', 'DEPOSIT', 'WITHDRAWAL', 'TRANSFER', 'PAYMENT', 'REFUND'
]

function TransactionRuleBuilder() {
  const { register, handleSubmit, watch, setValue, reset } = useForm({
    defaultValues: {
      id: '',
      name: '',
      description: '',
      rationale: '',
      type: 'transaction',
      processing_category: 'session',
      severity: 'medium',
      // S6-F1: optional per-tier severity overrides. Empty object
      // serializes clean when the author hasn't opted in.
      severity_by_tier: {},
      enabled: true,
      conditions: [{ field: '', operator: '>', value: '' }],
      actions: ['ALERT', 'CREATE_CASE']
    }
  })

  const [conditions, setConditions] = useState([{ field: '', operator: '>', value: '' }])
  const [timeWindowDays, setTimeWindowDays] = useState(1)
  const processingCategory = watch('processing_category')
  const [isTestingRule, setIsTestingRule] = useState(false)
  const [testResults, setTestResults] = useState(null)
  const [testMode, setTestMode] = useState('single') // 'single' | 'custom' | 'bulk'
  const [testDataText, setTestDataText] = useState('')
  const [bulkSize, setBulkSize] = useState(5)
  const location = useLocation()

  // Group fields by category
  const groupedFields = TRANSACTION_FIELDS.reduce((acc, field) => {
    if (!acc[field.category]) acc[field.category] = []
    acc[field.category].push(field)
    return acc
  }, {})

  // Add condition
  const addCondition = () => {
    setConditions([...conditions, { field: '', operator: '>', value: '' }])
  }

  // Remove condition
  const removeCondition = (index) => {
    if (conditions.length > 1) {
      setConditions(conditions.filter((_, i) => i !== index))
    }
  }

  // Update condition
  const updateCondition = (index, key, value) => {
    const newConditions = [...conditions]
    newConditions[index][key] = value
    setConditions(newConditions)
  }

  // S6: per-tier threshold overrides. Sets condition.value_by_tier[tier];
  // clearing a tier deletes its key; clearing all tiers drops the whole
  // value_by_tier object so existing rules serialize unchanged.
  const [tierOpen, setTierOpen] = useState({})
  const [severityTierOpen, setSeverityTierOpen] = useState(false)
  const updateConditionTier = (index, tier, value) => {
    const newConditions = [...conditions]
    const c = { ...newConditions[index] }
    const byTier = { ...(c.value_by_tier || {}) }
    if (value === '' || value === null || value === undefined) delete byTier[tier]
    else byTier[tier] = Number(value)
    if (Object.keys(byTier).length === 0) delete c.value_by_tier
    else c.value_by_tier = byTier
    newConditions[index] = c
    setConditions(newConditions)
  }

  // Toggle expression mode for a condition
  const toggleExpressionMode = (index) => {
    const next = [...conditions]
    const c = next[index]
    if (c && c.expression !== undefined) {
      next[index] = { field: '', operator: '>', value: '' }
    } else {
      next[index] = { expression: '' }
    }
    setConditions(next)
  }

  // Load rule details if id is provided in query
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const rid = params.get('id')
    if (!rid) return
    const load = async () => {
      try {
        const resp = await authFetch(getLocalApiUrl(`/api/rules/${encodeURIComponent(rid)}`))
        if (!resp.ok) return
        const json = await resp.json()
        const r = json?.data
        if (!r) return
        reset()
        setValue('id', r.id || '')
        setValue('name', r.name || '')
        setValue('description', r.description || '')
        setValue('type', 'transaction')
        setValue('processing_category', (() => {
          const pc = String(r.processing_category || '').toLowerCase().replace(/_/g, '-')
          if (pc === 'cumulative') return 'cumulative'
          if (pc === 'interval' || pc === 'interval-based' || pc === 'transaction-based') return 'interval'
          return 'session'
        })())
        setValue('severity', (r.severity || 'medium').toLowerCase())
        // Normalize conditions array (including expressions; DB may use a top-level array)
        let conds = []
        const c = r.conditions
        if (c != null && typeof c === 'object') {
          if (Array.isArray(c)) {
            conds = c
          } else if (c.type && Array.isArray(c.conditions)) {
            conds = c.conditions
          } else {
            conds = [c]
          }
        }
        const norm = conds.map(ci => (ci && ci.expression !== undefined ? { expression: String(ci.expression) } : {
          field: ci?.field || '',
          operator: mapBackendToUiOperator(ci?.operator || '>'),
          value: ci?.value ?? ''
        }))
        setConditions(norm.length ? norm : [{ field: '', operator: '>', value: '' }])
      } catch (e) {
        // ignore
      }
    }
    load()
  }, [location.search, reset, setValue])

  // Test rule against backend
  const testRule = async () => {
    setIsTestingRule(true)
    setTestResults(null)
    try {
      const id = (watch('id') || '').trim()
      if (!id) {
        alert('Please provide Rule ID before testing')
        return
      }
      let payload = { testType: testMode }
      if (testMode === 'custom') {
        try {
          const parsed = JSON.parse(testDataText || '[]')
          payload.data = parsed
        } catch (e) {
          alert('Invalid JSON in Test Data')
          return
        }
      }
      if (testMode === 'single') {
        try {
          const parsed = JSON.parse(testDataText || '{}')
          payload.data = parsed
        } catch (e) {
          // fallback minimal single example from first condition
          const c = conditions[0] || {}
          payload.data = c.field ? { [c.field]: c.value } : {}
        }
      }
      if (testMode === 'bulk') {
        payload.batchSize = Math.max(1, Number(bulkSize) || 5)
      }
      const url = getLocalApiUrl(`/api/rules/${encodeURIComponent(id)}/test`)
      const resp = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!json.success) {
        alert('Rule test failed: ' + (json.error || 'Unknown error'))
        return
      }
      setTestResults(json.data)
    } catch (error) {
      console.error('Error testing rule:', error)
      alert('Error testing rule: ' + error.message)
    } finally {
      setIsTestingRule(false)
    }
  }

  const onSubmit = async (data) => {
    // S3: policy rationale is required before a rule can be saved.
    const rationale = String(data.rationale || '').trim()
    if (rationale.length < 20) {
      alert('Please provide a rationale of at least 20 characters explaining why this rule exists and what risk it addresses. This is required by SVCP §5.3.')
      return
    }
    data.rationale = rationale
    // Support expression rows
    const out = conditions.map(c => {
      if (c.expression !== undefined) return { expression: String(c.expression) }
      const row = { field: c.field, operator: c.operator, value: c.value }
      // S6: pass the per-tier overrides through so the evaluator can apply them.
      if (c.value_by_tier && typeof c.value_by_tier === 'object' && Object.keys(c.value_by_tier).length > 0) {
        row.value_by_tier = c.value_by_tier
      }
      return row
    })
    data.conditions = out.length > 1 ? { type: 'and', conditions: out } : out[0]
    // If cumulative, attach time window metadata
    if (processingCategory === 'cumulative') {
      const days = Number(timeWindowDays) || 1
      data.time_window_days = days
      // Also provide aggregation hint for the engine (e.g., ['2d'])
      data.aggregation = Array.from(new Set([...(data.aggregation || []), `${days}d`]))
    }
    // Normalize and save to backend
    const payload = {
      ...data,
      processing_category:
        data.processing_category === 'session'
          ? 'session-based'
          : data.processing_category === 'interval'
            ? 'interval-based'
            : data.processing_category,
      type: 'transaction',
    }
    // S6-F1: strip severity_by_tier when empty so rules without overrides
    // serialize identically to pre-S6-F1 rules.
    if (
      payload.severity_by_tier &&
      typeof payload.severity_by_tier === 'object' &&
      Object.keys(payload.severity_by_tier).length === 0
    ) {
      delete payload.severity_by_tier
    }
    try {
      const method = data.id ? 'PUT' : 'POST'
      const url = data.id ? getLocalApiUrl(`/api/rules/${encodeURIComponent(data.id)}`) : getLocalApiUrl('/api/rules')
      const resp = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      const json = await resp.json()
      if (!json.success) {
        alert('Failed to save rule: ' + (json.error || 'Unknown error'))
      } else {
        alert('Rule saved')
      }
    } catch (e) {
      alert('Failed to save rule: ' + e.message)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link 
                to="/monitor-safe/builder"
                className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-500" />
              </Link>
              <DocumentTextIcon className="w-8 h-8 text-blue-600 mr-3" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Transaction Rule Builder</h1>
                <p className="text-gray-600">Create rules for monitoring financial transactions</p>
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={testRule}
                disabled={isTestingRule}
                className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                <BeakerIcon className="w-4 h-4 mr-2" />
                {isTestingRule ? 'Testing...' : 'Test Rule'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Rule Configuration */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rule ID</label>
                    <input
                      {...register('id', { required: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="TXN_001"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Rule Name</label>
                    <input
                      {...register('name', { required: true })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Large Cash Transaction"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      {...register('description')}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Detects large cash transactions that may require additional scrutiny"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Rationale <span className="text-red-500">*</span>
                        <span className="text-gray-400 font-normal ml-1 text-xs">
                          (why this rule exists, what risk it addresses, evidence behind thresholds)
                        </span>
                      </label>
                      <span className={`text-xs font-mono ${(watch('rationale') || '').trim().length >= 20 ? 'text-green-600' : 'text-gray-400'}`}>
                        {(watch('rationale') || '').trim().length} / 20 min
                      </span>
                    </div>
                    <textarea
                      {...register('rationale')}
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder={'e.g. "Addresses structuring risk RA-2026-037. Threshold $9,500 set just below the $10k TTR cutoff; evidence: 3 of last 6 quarters showed members depositing multi-venue amounts totalling $9.5k+ over 24h."'}
                    />
                    {(watch('rationale') || '').trim().length > 0 && (watch('rationale') || '').trim().length < 20 && (
                      <p className="mt-1 text-xs text-red-600">
                        Minimum 20 characters required before this rule can be submitted.
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Evaluation grain</label>
                    <select
                      {...register('processing_category')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {PROCESSING_CATEGORY_LEGACY_FORM_OPTIONS.map((category) => (
                        <option key={category.value} value={category.value}>
                          {category.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {processingCategory === 'cumulative' && (
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cumulative Time Window</label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Presets</label>
                          <select
                            value={String(timeWindowDays)}
                            onChange={(e) => setTimeWindowDays(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            {[1,2,7,15,30,90].map((d) => (
                              <option key={d} value={d}>{d} day{d>1?'s':''}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Or enter days</label>
                          <input
                            type="number"
                            min={1}
                            value={timeWindowDays}
                            onChange={(e) => setTimeWindowDays(Math.max(1, Number(e.target.value)))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="e.g., 45"
                          />
                        </div>
                        <div className="flex items-end">
                          <div className="text-sm text-gray-600">Evaluates totals over the last <span className="font-medium">{timeWindowDays}</span> day{timeWindowDays>1?'s':''}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Severity</label>
                    <select
                      {...register('severity')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="critical">Critical</option>
                    </select>
                    {/* S6-F1: opt-in per-tier severity */}
                    {(() => {
                      const byTier = watch('severity_by_tier') || {}
                      const hasOverrides = Object.keys(byTier).length > 0
                      const panelOpen = severityTierOpen || hasOverrides
                      return (
                        <div className="mt-2">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                              onClick={() => setSeverityTierOpen(v => !panelOpen)}
                            >
                              {panelOpen ? '− Hide per-tier severity' : '+ Per-tier severity (S6-F1)'}
                            </button>
                            {hasOverrides && !severityTierOpen && (
                              <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                Tier severity active
                              </span>
                            )}
                          </div>
                          {panelOpen && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                              {['simplified','standard','enhanced','prohibited'].map(tier => (
                                <div key={tier}>
                                  <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                                  <select
                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={byTier[tier] || ''}
                                    onChange={(e) => {
                                      const next = { ...(byTier || {}) }
                                      if (!e.target.value) delete next[tier]
                                      else next[tier] = e.target.value
                                      setValue('severity_by_tier', next, { shouldDirty: true })
                                    }}
                                  >
                                    <option value="">(base)</option>
                                    <option value="low">Low</option>
                                    <option value="medium">Medium</option>
                                    <option value="high">High</option>
                                    <option value="critical">Critical</option>
                                  </select>
                                </div>
                              ))}
                              <p className="col-span-2 md:col-span-4 text-[10px] text-gray-500 italic">
                                When a patron's CDD tier (S2) matches, the tier severity replaces the base on the published alert. Leave as (base) to fall back.
                              </p>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </div>

              {/* Linked Risks — S10 */}
              <div className="bg-white rounded-lg shadow p-6">
                <RuleRiskLinksPanel ruleId={watch('id') || ''} />
              </div>

              {/* Conditions */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Rule Conditions</h3>
                  <button
                    type="button"
                    onClick={addCondition}
                    className="flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-1" />
                    Add Condition
                  </button>
                </div>

                <div className="space-y-4">
                  {conditions.map((condition, index) => (
                    <div key={index} className="p-4 border border-gray-200 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-xs text-gray-600">Condition {index + 1}</div>
                        <div className="flex items-center space-x-2">
                          <label className="flex items-center text-xs text-gray-600">
                            <input
                              type="checkbox"
                              checked={condition.expression !== undefined}
                              onChange={() => toggleExpressionMode(index)}
                              className="mr-2"
                            />
                            Use expression
                          </label>
                          {conditions.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeCondition(index)}
                              className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Remove condition"
                            >
                              <TrashIcon className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>

                      {condition.expression !== undefined ? (
                        <input
                          type="text"
                          value={condition.expression}
                          onChange={(e) => updateCondition(index, 'expression', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="e.g., (${CashDrop} - ${Bill10}) >= 500 && (${CashDrop} % 5) == 0"
                        />
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <select
                              value={condition.field}
                              onChange={(e) => updateCondition(index, 'field', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              <option value="">Select Field</option>
                              {Object.entries(groupedFields).map(([category, fields]) => (
                                <optgroup key={category} label={category}>
                                  {fields.map((field) => (
                                    <option key={field.id} value={field.id}>
                                      {field.label}
                                    </option>
                                  ))}
                                </optgroup>
                              ))}
                            </select>
                          </div>
                          <div>
                            <select
                              value={condition.operator}
                              onChange={(e) => updateCondition(index, 'operator', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            >
                              {OPERATORS.map((op) => (
                                <option key={op.value} value={op.value}>
                                  {op.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <input
                              type="text"
                              value={condition.value}
                              onChange={(e) => updateCondition(index, 'value', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="Value"
                            />
                          </div>
                        </div>
                      )}
                      {/* S6: per-tier thresholds — numeric operators only */}
                      {condition.expression === undefined && ['>','>=','<','<=','between','greater_than','greater_than_or_equal','less_than','less_than_or_equal'].includes(condition.operator) && (() => {
                        const hasOverrides = condition.value_by_tier && Object.keys(condition.value_by_tier).length > 0
                        const tierPanelOpen = !!tierOpen[index] || hasOverrides
                        return (
                          <div className="mt-3 pt-3 border-t border-gray-100">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                                onClick={() => setTierOpen(prev => ({ ...prev, [index]: !tierPanelOpen }))}
                              >
                                {tierPanelOpen ? '− Hide per-tier thresholds' : '+ Per-tier thresholds (S6)'}
                              </button>
                              {hasOverrides && !tierOpen[index] && (
                                <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                  Tier overrides active
                                </span>
                              )}
                            </div>
                            {tierPanelOpen && (
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                                {['simplified','standard','enhanced','prohibited'].map(tier => (
                                  <div key={tier}>
                                    <label className="text-[10px] text-gray-500 uppercase tracking-wider block mb-0.5">{tier}</label>
                                    <input
                                      type="number"
                                      step="any"
                                      placeholder={condition.value !== '' && condition.value != null ? String(condition.value) : '(base)'}
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      value={condition.value_by_tier?.[tier] ?? ''}
                                      onChange={(e) => updateConditionTier(index, tier, e.target.value)}
                                    />
                                  </div>
                                ))}
                                <p className="col-span-2 md:col-span-4 text-[10px] text-gray-500 italic">
                                  When a patron's CDD tier (S2) matches, the tier value is used instead of the base value. Leave blank to fall back to the base.
                                </p>
                              </div>
                            )}
                          </div>
                        )
                      })()}
                    </div>
                  ))}
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                >
                  Create Transaction Rule
                </button>
              </div>
            </form>
          </div>

          {/* Test Results Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Results</h3>
              
              {/* Test Controls */}
              <div className="space-y-3 mb-4">
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <select className="border rounded px-2 py-1" value={testMode} onChange={(e)=>setTestMode(e.target.value)}>
                    <option value="single">Single</option>
                    <option value="custom">Custom Dataset</option>
                    <option value="bulk">Bulk (generated)</option>
                  </select>
                  {testMode === 'bulk' && (
                    <input className="border rounded px-2 py-1" type="number" min={1} value={bulkSize} onChange={(e)=>setBulkSize(e.target.value)} placeholder="Batch size" />
                  )}
                </div>
                {(testMode === 'single' || testMode === 'custom') && (
                  <textarea className="w-full border rounded px-2 py-1 text-xs font-mono" rows={6} value={testDataText} onChange={(e)=>setTestDataText(e.target.value)} placeholder={testMode==='single' ? '{"MemberID":"M1","CashIn":12000}' : '[{"MemberID":"M1","CashIn":6000},{"MemberID":"M1","CashIn":5000}]'} />
                )}
              </div>

              {!testResults ? (
                <div className="text-center py-4">
                  <p className="text-gray-500 text-sm">Prepare test inputs above and click "Test Rule"</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-sm text-gray-700">{testResults.ruleName} ({testResults.ruleId})</div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div>Records: <span className="font-medium">{testResults.summary?.totalRecords}</span></div>
                    <div>Triggered: <span className="font-medium">{testResults.summary?.triggeredCount}</span></div>
                    <div>Time: <span className="font-medium">{testResults.summary?.processingTime}ms</span></div>
                  </div>
                  <div className="space-y-2 max-h-64 overflow-auto">
                    {(testResults.results || []).map((r, idx) => (
                      <div key={idx} className={`p-2 rounded border text-xs ${r.triggered ? 'border-green-200 bg-green-50' : 'border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                          <div className="font-mono truncate">{JSON.stringify(r.input)}</div>
                          <div className={`ml-2 ${r.triggered ? 'text-green-700' : 'text-gray-500'}`}>{r.triggered ? 'Triggered' : 'No'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TransactionRuleBuilder